/**
 * Unit conversion, done once, at the parser boundary.
 *
 * Everything downstream of a parser is feet, knots and degrees — the units a
 * pilot reads and the units `TrackPoints` declares. A source that speaks metres
 * or km/h converts here and nowhere else, so there is exactly one place a
 * factor can be wrong.
 */

/** Exact by definition since 1959. */
export const METRES_PER_FOOT = 0.3048;

/** Exact by definition: 1 nm = 1852 m. */
export const METRES_PER_NAUTICAL_MILE = 1852;

const EARTH_RADIUS_NM = 6_371_008.8 / METRES_PER_NAUTICAL_MILE;

export function metresToFeet(metres: number): number {
  return metres / METRES_PER_FOOT;
}

export function feetToMetres(feet: number): number {
  return feet * METRES_PER_FOOT;
}

export function metresPerSecondToKnots(mps: number): number {
  return (mps * 3600) / METRES_PER_NAUTICAL_MILE;
}

export function kmhToKnots(kmh: number): number {
  return (kmh * 1000) / METRES_PER_NAUTICAL_MILE;
}

export function mphToKnots(mph: number): number {
  return mph * 0.868_976_241_9;
}

/**
 * Great-circle distance in nautical miles.
 *
 * Haversine on a spherical earth: about 0.3% out at worst, which is under a
 * tenth of a mile on any leg a light aircraft flies, and far inside the error
 * already present in the fixes themselves.
 */
export function haversineNm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const toRad = Math.PI / 180;
  const dLat = (lat2 - lat1) * toRad;
  const dLon = (lon2 - lon1) * toRad;
  const sinLat = Math.sin(dLat / 2);
  const sinLon = Math.sin(dLon / 2);
  const a = sinLat * sinLat + Math.cos(lat1 * toRad) * Math.cos(lat2 * toRad) * sinLon * sinLon;
  return 2 * EARTH_RADIUS_NM * Math.asin(Math.min(1, Math.sqrt(a)));
}

/**
 * Thousands separators, as characters rather than as escapes inside a regex
 * literal: comma, ordinary space, non-breaking space (U+00A0) and narrow
 * no-break space (U+202F). The last two are what a French or Nordic locale
 * groups with, and they are invisible in a source file — built here from code
 * points so nobody has to trust what a diff looks like.
 */
const GROUPING = `,${String.fromCharCode(0x20, 0x00a0, 0x202f)}`;
const LOOSE_NUMBER = new RegExp(`-?\\d[\\d${GROUPING}]*(?:\\.\\d+)?`);
const GROUPING_CHARS = new RegExp(`[${GROUPING}]`, 'g');

/**
 * A number as a source wrote it: thousands separators, a unit suffix, HTML
 * entities, a stray plus sign, or nothing at all.
 *
 * Flightradar24's KML writes altitude as `1,425 ft`, so a plain `Number()` here
 * returns `NaN` and silently loses the altitude of the entire flight.
 *
 * @returns the value, or `NaN` if there is no number in the string.
 */
export function parseLooseNumber(raw: string | null | undefined): number {
  if (raw === null || raw === undefined) {
    return Number.NaN;
  }
  const match = LOOSE_NUMBER.exec(raw.trim());
  if (!match) {
    return Number.NaN;
  }
  // Strip the grouping separators but keep the decimal point.
  const value = Number(match[0].replace(GROUPING_CHARS, ''));
  return Number.isFinite(value) ? value : Number.NaN;
}
